/**
 * Per-user pinned sidebar items (spec section 8, "★ Pinned").
 * Per-browser, connection-scoped localStorage — same persistence tier as
 * theme mode and view prefs.
 */
import { useCallback, useEffect, useState } from "react";
import { prefsStore } from "@/app/preferences/prefsStore";

export interface SidebarPin {
  path: string;
  text: string;
}

const keyFor = (connectionId: string | null | undefined) =>
  `sidebar-pins:${connectionId ?? "none"}`;

function load(connectionId: string | null | undefined): SidebarPin[] {
  try {
    const raw = prefsStore.getItem(keyFor(connectionId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is SidebarPin =>
        Boolean(p) && typeof (p as SidebarPin).path === "string" && typeof (p as SidebarPin).text === "string"
    );
  } catch {
    return [];
  }
}

export function useSidebarPins(connectionId: string | null | undefined) {
  const [pins, setPins] = useState<SidebarPin[]>(() => load(connectionId));

  useEffect(() => {
    setPins(load(connectionId));
  }, [connectionId]);

  const isPinned = useCallback((path: string) => pins.some((p) => p.path === path), [pins]);

  const togglePin = useCallback(
    (pin: SidebarPin) => {
      setPins((prev) => {
        const next = prev.some((p) => p.path === pin.path)
          ? prev.filter((p) => p.path !== pin.path)
          : [...prev, pin];
        try {
          prefsStore.setItem(keyFor(connectionId), JSON.stringify(next));
        } catch {
          /* private browsing */
        }
        return next;
      });
    },
    [connectionId]
  );

  return { pins, isPinned, togglePin };
}
